import { type QuestionFlipboard } from 'api/types/ChapterContent/QuestionFlipboard';
import { type RawQuestionFlipboard } from 'api/types/ChapterContent/QuestionFlipboard/RawQuestionFlipboard';
import { type QuestionSlider } from 'api/types/ChapterContent/QuestionSlider';
import { type RawQuestionSlider } from 'api/types/ChapterContent/QuestionSlider/RawQuestionSlider';

const getOptions = (
  steps: RawQuestionSlider['steps'],
  startNumber: RawQuestionSlider['startNumber'],
  endNumber: RawQuestionSlider['endNumber'],
  rawAnswer: RawQuestionSlider['correctAnswer'],
) => {
  const answer = Number(rawAnswer);

  const options = steps.filter((step): step is number => {
    return step !== null;
  });

  const [firstStep] = options;
  const [lastStep] = options.slice(-1);

  if (endNumber > lastStep) {
    options.push(endNumber);
  }

  if (startNumber < firstStep) {
    options.unshift(startNumber);
  }

  if (!options.includes(answer)) {
    options.forEach((step, i) => {
      const nextStep = options[i + 1];

      if (nextStep !== undefined && nextStep > answer && step < answer) {
        options.splice(i + 1, 0, answer);
      }
    });
  }

  return Array.from(new Set(options));
};

export const parseSlider = (
  data: RawQuestionSlider | RawQuestionFlipboard,
): Omit<QuestionSlider | QuestionFlipboard, 'image'> => {
  const options = getOptions(data.steps, data.startNumber, data.endNumber, data.correctAnswer);

  const output: Omit<QuestionSlider | QuestionFlipboard, 'image'> = {
    correctAnswer: Number(data.correctAnswer),
    correctAnswerText: data.correctAnswerText || '',
    id: data.UPID,
    incorrectAnswerText: data.incorrectAnswerText,
    options,
    pageNumber: data.pageNumber,
    question: data.question,
    title: data.question,
    type: 'QuestionFlipboard',
  };

  if (data.layout === 's') {
    return {
      ...output,
      decimals: Boolean(data.decimals),
      type: 'QuestionSlider',
    } as Omit<QuestionSlider, 'image'>;
  }

  return output;
};
